import { ref } from 'vue'
import { useLogger } from '@booksy-com/vue-tools'

type LocalizeParams = {
  country?: string
  language?: string
}

type QueryValue = string | number | boolean | null | undefined
type QueryObject = Record<string, QueryValue>

type UrlBuilder = {
  removeTrailingSlash: () => UrlBuilder
  addLocalize: (params?: LocalizeParams) => UrlBuilder
  addPath: (path?: string) => UrlBuilder
  addQueryObject: (query?: QueryObject) => UrlBuilder
  build: () => string
}

export function useUrlBuilder (initialValue: string): UrlBuilder {
  const src = ref(initialValue)
  const logger = useLogger('url-builder')

  const builder: UrlBuilder = {
    removeTrailingSlash: () => {
      if (!src.value) {
        return builder
      }

      src.value = src.value.replace(/\/+$/, '')

      return builder
    },
    addLocalize: ({ country, language }: LocalizeParams = {}) => {
      if (!country || !language) {
        logger.warn('localize param is not defined')
        return builder
      }

      src.value += `/${encodeURIComponent(language)}-${encodeURIComponent(country)}/`

      return builder
    },
    addPath: (path?: string) => {
      if (!path) {
        logger.warn('path is not defined')
        return builder
      }

      const hasTrailingSlash = src.value[src.value.length - 1] === '/'

      if (hasTrailingSlash) {
        builder.removeTrailingSlash()
      }

      src.value += String(path)
        .split('/')
        .reduce((acc, part) => {
          if (!part?.trim()?.length) {
            return acc
          }

          acc += `/${encodeURIComponent(part)}`
          return acc
        }, '')

      return builder
    },
    addQueryObject: (query?: QueryObject) => {
      if (!query) {
        logger.warn('query is not defined')
        return builder
      }

      if (!Object.keys(query).length) {
        logger.info('empty query object provided')
        return builder
      }

      const hasSrcQueryParams = src.value.includes('?')
      const queryString = new URLSearchParams(query as Record<string, string>).toString()

      src.value += hasSrcQueryParams ? `&${queryString}` : `?${queryString}`

      return builder
    },
    build: () => {
      const value = src.value
      src.value = initialValue

      return value
    },
  }

  return builder
}
